import React, { useState, Fragment } from 'react';
import { ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
import { ProductionStep } from './ProductionStep';
import { WeighingProcess } from './WeighingProcess';
interface ProductionOrderProps {
  orderId: string;
  onReset: () => void;
  savedProgress?: {
    currentStepIndex: number;
    completedIngredients: string[];
  };
  onSaveProgress: (progress: {
    currentStepIndex: number;
    completedIngredients: string[];
  }) => void;
}
// Mock-Daten für einen Produktionsauftrag
const mockProductionData = {
  '130901230425': {
    name: 'Palettenetikett',
    steps: [{
      id: 'step1',
      name: 'Kutterei',
      ingredients: [{
        id: 'ing1',
        name: 'Schweinefleisch S1|gekühlt',
        amount: 500,
        unit: 'kg',
        tolerance: 5,
        requiresWeighing: true
      }, {
        id: 'ing2',
        name: 'Starterkulturen K7',
        amount: 250,
        unit: 'kg',
        tolerance: 0,
        requiresWeighing: false,
        packageSize: '250kg Sack'
      }, {
        id: 'ing3',
        name: 'Würzmischung Classic Plus',
        amount: 4.88,
        unit: 'kg',
        tolerance: 0,
        requiresWeighing: false,
        packageSize: '4.88kg Beutel'
      }, {
        id: 'ing4',
        name: 'Pökelsalz Standard',
        amount: 4.78,
        unit: 'kg',
        tolerance: 0,
        requiresWeighing: false,
        packageSize: '4.78kg Beutel'
      }]
    }, {
      id: 'step2',
      name: 'Füllerei',
      ingredients: [{
        id: 'ing5',
        name: 'Naturdarm 28/30',
        amount: 25,
        unit: 'Bund',
        tolerance: 0
      }]
    }, {
      id: 'step3',
      name: 'Räucherei',
      ingredients: [{
        id: 'ing6',
        name: 'Smoke Liquid Classic',
        amount: 2.5,
        unit: 'L',
        tolerance: 0.1,
        requiresWeighing: false,
        packageSize: '2.5L Kanister'
      }]
    }, {
      id: 'step4',
      name: 'Schälerei',
      ingredients: [{
        id: 'ing7',
        name: 'Palettenetikett',
        amount: 8,
        unit: 'Chargen',
        tolerance: 0,
        requiresWeighing: false,
        isMultiScan: true,
        scannedCount: 0,
        maxScans: 8 // Maximale Anzahl von Chargen
      }]
    },
    // Neuer Verpackungsschritt
    {
      id: 'step5',
      name: 'Verpackung',
      ingredients: [{
        id: 'ing8',
        name: 'Verpackungsetikett',
        amount: 1,
        unit: 'Scan',
        tolerance: 0,
        requiresWeighing: false,
        isPackagingLabel: true,
        description: 'Scannen Sie entweder den Kundenbarcode oder das Chargenetikett'
      }]
    }]
  },
  '130901230426': {
    name: 'Cabanossi Classic',
    steps: [{
      id: 'step1',
      name: 'Kutterei',
      ingredients: [{
        id: 'ing1',
        name: 'Schweinefleisch S II',
        amount: 450,
        unit: 'kg',
        tolerance: 4.5
      }, {
        id: 'ing2',
        name: 'Rückenspeck',
        amount: 200,
        unit: 'kg',
        tolerance: 2
      }, {
        id: 'ing3',
        name: 'Gewürzmischung Cabanossi',
        amount: 5.5,
        unit: 'kg',
        tolerance: 0.1
      }]
    }, {
      id: 'step2',
      name: 'Füllerei',
      ingredients: []
    }]
  }
};
export const ProductionOrder = ({
  orderId,
  onReset,
  savedProgress,
  onSaveProgress
}: ProductionOrderProps) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(savedProgress?.currentStepIndex || 0);
  const [completedIngredients, setCompletedIngredients] = useState<string[]>(savedProgress?.completedIngredients || []);
  const [activeIngredientId, setActiveIngredientId] = useState<string | null>(null);
  const [ingredientBatches, setIngredientBatches] = useState<{
    [key: string]: string;
  }>({});
  const orderData = mockProductionData[orderId] || {
    name: `Charge ${orderId}`,
    steps: [{
      id: 'step1',
      name: 'Kutterei',
      ingredients: [{
        id: 'ing1',
        name: 'Schulter|TW/QSgefr',
        amount: 500,
        unit: 'kg',
        tolerance: 5,
        requiresWeighing: true
      }, {
        id: 'ing2',
        name: 'Maturation Starter',
        amount: 250,
        unit: 'kg',
        tolerance: 0,
        requiresWeighing: false,
        packageSize: '250kg Sack'
      }, {
        id: 'ing3',
        name: 'Gewürzmischung Pfefferbeisserpikant',
        amount: 4.88,
        unit: 'kg',
        tolerance: 0,
        requiresWeighing: false,
        packageSize: '4.88kg Beutel'
      }, {
        id: 'ing4',
        name: 'NPS 0,4 - 0,5',
        amount: 4.78,
        unit: 'kg',
        tolerance: 0,
        requiresWeighing: false,
        packageSize: '4.78kg Beutel'
      }]
    }, {
      id: 'step2',
      name: 'Füllerei',
      ingredients: [{
        id: 'ing3',
        name: 'Naturdarm 28/30',
        amount: 25,
        unit: 'Bund',
        tolerance: 0
      }]
    }]
  };
  const currentStep = orderData.steps[currentStepIndex];
  const handleCompleteIngredient = (ingredientId: string, batchNumber?: string) => {
    setCompletedIngredients([...completedIngredients, ingredientId]);
    if (batchNumber) {
      setIngredientBatches(prev => ({
        ...prev,
        [ingredientId]: batchNumber
      }));
    }
    setActiveIngredientId(null);
  };
  const isStepComplete = currentStep?.ingredients.every(ing => completedIngredients.includes(ing.id)) || currentStep?.ingredients.length === 0;
  const handleNextStep = () => {
    // Speichere Fortschritt und erhöhe Step Index
    const nextIndex = currentStepIndex + 1;
    onSaveProgress({
      currentStepIndex: nextIndex,
      completedIngredients
    });
    onReset();
  };
  const isLastStep = currentStepIndex === orderData.steps.length - 1;
  const handleFinishOrder = () => {
    // Lösche den Fortschritt beim Abschließen
    onSaveProgress({
      currentStepIndex: 0,
      completedIngredients: []
    });
    alert('Auftrag abgeschlossen!');
    onReset();
  };
  return <div className="flex flex-col h-full" data-id="element-71">
      {/* Header Info */}
      <div className="bg-white rounded-lg shadow-md p-3 mb-2" data-id="element-72">
        <h2 className="text-base font-bold truncate" data-id="element-73">
          {currentStep.name === 'Schälerei' ? 'Palettenetikett' : currentStep.name === 'Verpackung' ? 'Meyer GmbH' : 'Rohbratwurst'}
        </h2>
        <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-74">
          {currentStep.name === 'Schälerei' ? <span data-id="element-75">Palette: 600438</span> : currentStep.name === 'Verpackung' ? <span data-id="element-76">Auftrag: KA-887987997</span> : <span data-id="element-77">Charge: 887987997</span>}
        </div>
      </div>
      {/* Progress Steps */}
      <div className="bg-white rounded-lg shadow-md p-2 mb-2" data-id="element-78">
        <div className="flex items-center gap-0.5 text-xs w-full overflow-x-auto whitespace-nowrap" data-id="element-79">
          {orderData.steps.map((step, index) => <Fragment key={step.id} data-id="element-80">
              <div className={`px-2 py-1 rounded-full flex-shrink-0 ${index === currentStepIndex ? 'bg-[#e76405] text-white font-medium' : index < currentStepIndex ? 'bg-green-100 text-green-700' : 'text-gray-400'}`} data-id="element-81">
                {step.name}
              </div>
              {index < orderData.steps.length - 1 && <div className="text-gray-300 mx-0.5 flex-shrink-0" data-id="element-82">→</div>}
            </Fragment>)}
        </div>
      </div>
      {/* Main Content */}
      <div className="flex-1 min-h-0" data-id="element-83">
        <div className="bg-white rounded-lg shadow-md p-3 h-full flex flex-col" data-id="element-84">
          <div className="flex-1" data-id="element-85">
            {activeIngredientId ? <WeighingProcess ingredient={currentStep.ingredients.find(ing => ing.id === activeIngredientId)!} onComplete={batchNumber => handleCompleteIngredient(activeIngredientId, batchNumber)} onCancel={() => setActiveIngredientId(null)} data-id="element-86" /> : <ProductionStep step={currentStep} completedIngredients={completedIngredients} ingredientBatches={ingredientBatches} onSelectIngredient={setActiveIngredientId} onComplete={handleCompleteIngredient} orderId={orderId} data-id="element-87" />}
          </div>
          {/* Action Buttons */}
          <div className="mt-4" data-id="element-88">
            {isStepComplete && !activeIngredientId && <button onClick={isLastStep ? handleFinishOrder : handleNextStep} className="w-full bg-[#e76405] text-white py-3 px-4 rounded-lg text-base font-medium flex items-center justify-center gap-2 mb-3" data-id="element-89">
                <CheckCircleIcon size={20} data-id="element-90" />
                {isLastStep ? 'Auftrag abschließen' : 'Prozessschritt abschließen'}
              </button>}
            <button onClick={onReset} className="w-full bg-gray-100 text-gray-600 py-3 px-4 rounded-lg text-base font-medium" data-id="element-91">
              Zurück
            </button>
          </div>
        </div>
      </div>
    </div>;
};